import { useCallback, useMemo } from 'react';
import { DefaultValue, atom, selectorFamily, useRecoilValue, useRecoilValueLoadable, useSetRecoilState } from 'recoil';
import { getPersistedValue, setPersistedValue } from '../db/config';
import { trackEvent } from '../tracking';

export const colorSchemeOptions = ['default', 'dark', 'light'] as const;
export type ColorScheme = (typeof colorSchemeOptions)[number];

export const fontSizeOptions = [
  { id: 'small', fontSize: 14 },
  { id: 'medium', fontSize: 16 },
  { id: 'large', fontSize: 20 },
] as const;
export type FontSize = (typeof fontSizeOptions)[number]['id'];

export const coordinatesDisplayOptions = [{ id: 'wgs-decimal' }, { id: 'wgs-degrees' }, { id: 'utm' }, { id: 'mgrs' }, { id: 'what3words' }];
export type CoordinatesDisplayType = (typeof coordinatesDisplayOptions)[number]['id'];

export const deviceLabelOptions = ['name', 'identifier'] as const;
export type DeviceLabelOption = (typeof deviceLabelOptions)[number];

export const resourceListDisplayOptions = ['cards', 'table'] as const;
export type ResourceListDisplay = (typeof resourceListDisplayOptions)[number];

export type Settings = {
  colorScheme: ColorScheme;
  fontSize: FontSize;
  playAlarmSound: boolean;
  screensaverEnabled: boolean;
  screensaverTimeout: number;
  coordinatesDisplayTypes: Array<CoordinatesDisplayType>;
  speechVoiceURI?: string;
  speechLanguage?: string;
  speechRate: number;
  announceNewMission: boolean;
  announceNewMissionResource: boolean;
  announceMissionResourceStatusChange: boolean;
  announceNewMissionReport: boolean;
  announceNewMissionPatient: boolean;
  deviceLabel: DeviceLabelOption;
  tableauResourceListDisplay: ResourceListDisplay;
};

// TODO is there a nice way to extract boolean keys?
export type BooleanSettingKey =
  | 'playAlarmSound'
  | 'screensaverEnabled'
  | 'announceNewMission'
  | 'announceNewMissionResource'
  | 'announceMissionResourceStatusChange'
  | 'announceNewMissionReport'
  | 'announceNewMissionPatient';

const defaultSettings: Settings = {
  colorScheme: 'default',
  fontSize: 'medium',
  playAlarmSound: true,
  screensaverEnabled: true,
  screensaverTimeout: 300000,
  coordinatesDisplayTypes: ['wgs-decimal', 'wgs-degrees', 'utm', 'mgrs'],
  speechRate: 1,
  announceNewMission: true,
  announceNewMissionResource: true,
  announceMissionResourceStatusChange: true,
  announceNewMissionReport: true,
  announceNewMissionPatient: true,
  deviceLabel: 'name',
  tableauResourceListDisplay: 'cards',
};

export function getDefaultSetting<K extends keyof Settings>(key: K): Settings[K] {
  return defaultSettings[key];
}

const settingsAtom = atom<Settings>({
  key: 'settings',
  default: getPersistedValue('settings', defaultSettings).then((p) => ({ ...defaultSettings, ...p })),
});

const settingSelector = selectorFamily<any, keyof Settings>({
  key: 'settings',
  get:
    (param) =>
    ({ get }) =>
      get(settingsAtom)[param],
  set:
    (param) =>
    ({ set }, value) => {
      set(settingsAtom, (values) => {
        const next = { ...values, [param]: value instanceof DefaultValue ? getDefaultSetting(param) : value };
        setPersistedValue('settings', next);
        return next;
      });
    },
});

/**
 * Get a user setting. Warning, this method will throw if used outside of a Suspense!
 */
export function useSetting<K extends keyof Settings>(key: K): Settings[K] {
  return useRecoilValue(settingSelector(key));
}

/**
 * Get a user setting, falling back to default settings if the user settings have not been initialized yet.
 * It is safe to call this method outside of a Suspense.
 */
export function useSettingOrDefault<K extends keyof Settings>(key: K): Settings[K] {
  const value = useRecoilValueLoadable(settingSelector(key));
  return useMemo(() => (value.state === 'hasValue' ? value.contents : getDefaultSetting(key)), [key, value]);
}

export function useSetSetting<K extends keyof Settings>(key: K): (value: Settings[K]) => Promise<void> {
  const set = useSetRecoilState(settingSelector(key));

  return useCallback(
    async (value) => {
      trackEvent(`Setting: ${key}`, { category: 'Settings', label: value?.toString() });
      set(value);
    },
    [key, set]
  );
}
